export interface VirtualHistoryStack {
  /** Starts again from the entry that the browser is at, knowing none of the others */
  startAt: (key: string) => void,
  /**
   * Moves to an entry that was gone Back or Forward to. One that isn't known is from before the stack started (or from
   * before it gave up on the oldest). It can't be told which side of the current entry that is, so the stack starts
   * again from it, rather than guess.
   */
  traverseTo: (key: string) => void,
  /**
   * Adds a new entry straight after the current one, and makes it the current one. The entries that were after the
   * current one are gone, as the browser's are when it makes a new entry, and their keys are returned.
   */
  push: (key: string) => string[],
  /** Replaces the current entry with another, in place, so the entries after it are still there. Returns the key that it replaced. */
  replace: (key: string) => string
}

/**
 * Keeps track of the entries in the browser's history, and which of them the page is at. The browser doesn't say
 * either: it only gives the state of the entry that is current, so this has to be built up by watching the page move
 * through them. That means it only knows the entries seen since it was started, from where the page was at the time
 * onwards, and nothing from before then.
 *
 * Only the most recent entries, up to the limit, are known at a time, so that it doesn't grow for as long as the app
 * stays open. The ones that are given up on are still in the browser's history. They are just no longer known.
 */
export const createVirtualHistoryStack = (limit: number): VirtualHistoryStack => {
  // the keys of the known entries, in the order that they are in the browser's history, and the index of the current one
  let keys: string[] = [];
  let current = -1;

  const startAt = (key: string) => {
    keys = [key];
    current = 0;
  };

  return {
    startAt,
    traverseTo: key => {
      const index = keys.indexOf(key);
      if (index === -1) {
        startAt(key);
      } else {
        current = index;
      }
    },
    push: key => {
      const discarded = keys.splice(current + 1);
      keys.push(key);
      if (keys.length > limit) {
        keys.shift();
      }
      current = keys.length - 1;
      return discarded;
    },
    replace: key => {
      const replaced = keys[current];
      keys[current] = key;
      return replaced;
    }
  };
};
